import {
  addDays,
  Day,
  eachWeekOfInterval,
  endOfMonth,
  format,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import {Box, Center, Grid, Heading, Hide, Show} from 'platform/foundation';
import {convertStringLocaleToDateFns, DayOfWeek, useLocale} from 'platform/locale';
import {match} from 'ts-pattern';

import {always} from 'ramda';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {DAY_COLUMN_TEMPLATE} from '../constants/dayColumnTemplate';
import {DAYS_IN_WEEK} from '../constants/daysInWeek';
import type {BigCalendarEvent} from '../types/BigCalendarEvent';
import {MonthWeekRow} from './MonthWeekRow';

interface MonthViewProps extends RequiredTestIdProps {
  currentDate: Date;
  events: BigCalendarEvent[];
  isReadOnly?: boolean;
  onEventClick?: (event: BigCalendarEvent) => void;
  onDateClick?: (date: Date) => void;
  enableHover?: boolean;
  onEventMouseEnter?: (event: BigCalendarEvent, mouseEvent: React.MouseEvent) => void;
  onEventMouseLeave?: (event: BigCalendarEvent) => void;
}

export function MonthView(props: MonthViewProps) {
  const {language, localeConfig} = useLocale();

  const weekStartsOn = match<DayOfWeek, Day>(localeConfig.calendar.firstDayOfTheWeek)
    .with(DayOfWeek.MONDAY, always(1))
    .with(DayOfWeek.TUESDAY, always(2))
    .with(DayOfWeek.WEDNESDAY, always(3))
    .with(DayOfWeek.THURSDAY, always(4))
    .with(DayOfWeek.FRIDAY, always(5))
    .with(DayOfWeek.SATURDAY, always(6))
    .with(DayOfWeek.SUNDAY, always(0))
    .otherwise(always(1));

  const monthStart = startOfMonth(props.currentDate);
  const monthEnd = endOfMonth(props.currentDate);
  const monthStartWeek = startOfWeek(monthStart, {
    weekStartsOn,
  });

  const weekDays = Array.from({length: DAYS_IN_WEEK}, (_, i) => addDays(monthStartWeek, i));

  const weeks = eachWeekOfInterval(
    {
      start: monthStart,
      end: monthEnd,
    },
    {weekStartsOn}
  );

  return (
    <Box position="relative" width="100%" height="100%">
      <div className="overflow-auto [-webkit-overflow-scrolling:touch]">
        <Grid columns={DAY_COLUMN_TEMPLATE} spacing={0}>
          {weekDays.map((date, weekDayIndex) => (
            <Box
              key={`month-cell-heading-${date.toString()}`}
              height={10}
              paddingHorizontal={2}
              borderBottom="1px solid"
              borderRight={weekDayIndex === DAYS_IN_WEEK - 1 ? '1px solid' : 'none'}
              borderLeft="1px solid"
              borderColor="general.separator"
              position="relative"
            >
              <Center height="100%" justify="flex-start">
                <Show onMobile>
                  <Heading
                    size={4}
                    data-testid={suffixTestId(`monthView-dayHeader-[${weekDayIndex}]`, props)}
                  >
                    {format(date, 'E', {locale: convertStringLocaleToDateFns(language)})}
                  </Heading>
                </Show>
                <Hide onMobile>
                  <Heading
                    size={4}
                    data-testid={suffixTestId(`monthView-dayHeader-[${weekDayIndex}]`, props)}
                  >
                    {format(date, 'EEEE', {locale: convertStringLocaleToDateFns(language)})}
                  </Heading>
                </Hide>
              </Center>
            </Box>
          ))}
        </Grid>
        {weeks.map((week, weekIndex) => (
          <MonthWeekRow
            key={`month-week-${week.toISOString()}`}
            data-testid={suffixTestId(`monthView-[${weekIndex}]`, props)}
            weekStart={week}
            currentMonth={props.currentDate}
            events={props.events}
            onEventClick={props.onEventClick}
            enableHover={props.enableHover}
            onEventMouseEnter={props.onEventMouseEnter}
            onEventMouseLeave={props.onEventMouseLeave}
          />
        ))}
      </div>
    </Box>
  );
}
